import { useState } from 'react';
import {
  Button,
  Input,
  Textarea,
  Spinner,
  makeStyles,
  tokens,
  Text,
} from '@fluentui/react-components';
import { useUpdateReferralStatus } from '@/api/hooks/useReferrals';
import type { ReferralStatus } from '@/config/services';
import type { ReferralDto } from '@/types/referral';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
  inline: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'flex-end',
  },
});

function StatusActionButtons({
  status,
  disabled,
  onUpdate,
  onShowRevenue,
  onShowComment,
}: {
  status: ReferralDto['status'];
  disabled: boolean;
  onUpdate: (status: ReferralStatus) => void;
  onShowRevenue: () => void;
  onShowComment: (status: ReferralStatus) => void;
}) {
  switch (status) {
    case 'Submitted':
      return (
        <>
          <Button appearance="primary" size="small" onClick={() => onUpdate('UnderReview')} disabled={disabled}>
            Start Review
          </Button>
          <Button appearance="secondary" size="small" onClick={() => onShowComment('Disqualified')} disabled={disabled}>
            Disqualify
          </Button>
        </>
      );
    case 'UnderReview':
      return (
        <>
          <Button appearance="primary" size="small" onClick={() => onUpdate('Qualified')} disabled={disabled}>
            Qualify
          </Button>
          <Button appearance="secondary" size="small" onClick={() => onShowComment('Disqualified')} disabled={disabled}>
            Disqualify
          </Button>
        </>
      );
    case 'Qualified':
      return (
        <>
          <Button appearance="primary" size="small" onClick={() => onUpdate('InProgress')} disabled={disabled}>
            Start Working
          </Button>
          <Button appearance="secondary" size="small" onClick={() => onShowComment('Lost')} disabled={disabled}>
            Lost
          </Button>
        </>
      );
    case 'InProgress':
      return (
        <>
          <Button appearance="primary" size="small" onClick={() => onShowRevenue()} disabled={disabled}>
            Mark Won
          </Button>
          <Button appearance="secondary" size="small" onClick={() => onShowComment('Lost')} disabled={disabled}>
            Lost
          </Button>
        </>
      );
    case 'Won':
    case 'Lost':
    case 'Disqualified':
      return (
        <Button appearance="secondary" size="small" onClick={() => onUpdate('Submitted')} disabled={disabled}>
          Reopen
        </Button>
      );
    default:
      return null;
  }
}

interface QuickStatusActionsProps {
  referral: ReferralDto;
  performedBy: string;
  performedByName: string;
  onUpdated?: () => void;
}

export function QuickStatusActions({
  referral,
  performedBy,
  performedByName,
  onUpdated,
}: QuickStatusActionsProps) {
  const styles = useStyles();
  const updateStatus = useUpdateReferralStatus();
  const [showRevenue, setShowRevenue] = useState(false);
  const [revenue, setRevenue] = useState('');
  const [showComment, setShowComment] = useState<ReferralStatus | null>(null);
  const [comment, setComment] = useState('');

  const doUpdate = async (status: ReferralStatus, extra?: { revenue?: number; statusComments?: string }) => {
    await updateStatus.mutateAsync({
      id: referral.id,
      data: { status, ...extra },
      performedBy,
      performedByName,
    });
    setShowRevenue(false);
    setShowComment(null);
    setRevenue('');
    setComment('');
    onUpdated?.();
  };

  const confirmComment = async () => {
    if (showComment) {
      await doUpdate(showComment, { statusComments: comment.trim() || undefined });
    }
  };

  const confirmWon = async () => {
    await doUpdate('Won', { revenue: revenue ? parseFloat(revenue) : undefined });
  };

  return (
    <div className={styles.root}>
      <div className={styles.actions}>
        {updateStatus.isPending && <Spinner size="tiny" />}
        <StatusActionButtons
          status={referral.status}
          disabled={updateStatus.isPending}
          onUpdate={(status) => void doUpdate(status)}
          onShowRevenue={() => setShowRevenue(true)}
          onShowComment={setShowComment}
        />
      </div>

      {showRevenue && (
        <div className={styles.inline}>
          <Input
            size="small"
            type="number"
            placeholder="Revenue"
            value={revenue}
            onChange={(_e, d) => setRevenue(d.value)}
            contentBefore={<Text size={200}>$</Text>}
          />
          <Button appearance="primary" size="small" onClick={() => void confirmWon()} disabled={updateStatus.isPending}>
            Confirm
          </Button>
          <Button appearance="secondary" size="small" onClick={() => setShowRevenue(false)}>
            Cancel
          </Button>
        </div>
      )}

      {showComment && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS }}>
          <Textarea
            size="small"
            placeholder="Reason (optional)..."
            value={comment}
            onChange={(_e, d) => setComment(d.value)}
            rows={2}
          />
          <div className={styles.inline}>
            <Button appearance="primary" size="small" onClick={() => void confirmComment()} disabled={updateStatus.isPending}>
              Confirm
            </Button>
            <Button appearance="secondary" size="small" onClick={() => setShowComment(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
